import { ORDERSTATUS, ORDERTYPE } from "../../../models/order";

export type PromoFilter = {
  order_type?: string;
  order_status?: string;
};

export type StandalonePreset = {
  key: string;
  label: string;
  filter: PromoFilter;
  paramsOverride?: Record<string, any>;
  privilegedOnly?: boolean;
  /**
   * @deprecated No longer read. Admin and picker now both render the standard
   * OrdersContainer for the Manage preset; the separate handling container is
   * kept but unused. Left in place to avoid breaking the preset definitions.
   */
  rendersHandling?: boolean;
};

export const HANDLE_PRESET_KEY = "handle";
export const APPROVE_PRESET_KEY = "approve";
export const SEND_PRESET_KEY = "send";
// Promo always lands on Backorder; any reference to the removed "all" preset
// resolves here too (via resolvePreset's fallback).
export const DEFAULT_PRESET_KEY = "backorder";

export const TYPE_GROUPS: {
  prefix: string;
  label: string;
  orderType: string;
}[] = [
  { prefix: "backorder", label: "Backorder", orderType: ORDERTYPE.backorder },
];

export const STATUS_OPTIONS: {
  key: string;
  label: string;
  status?: string;
  privileged?: boolean;
}[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending", status: ORDERSTATUS.pending },
  { key: "ordered", label: "Ordered", status: ORDERSTATUS.ordered },
  {
    key: "in-progress",
    label: "In progress",
    status: ORDERSTATUS.inProgress,
    privileged: true,
  },
  { key: "shipped", label: "Shipped", status: ORDERSTATUS.shipped },
  { key: "cancelled", label: "Cancelled", status: ORDERSTATUS.cancelled },
];

export const STANDALONE_PRESETS: StandalonePreset[] = [
  {
    key: "backorder",
    label: "Backorder",
    filter: { order_type: ORDERTYPE.backorder },
  },
  {
    key: "original",
    label: "Original",
    filter: {
      order_type: ORDERTYPE.original,
      order_status: ORDERSTATUS.ordered,
    },
  },
  {
    key: HANDLE_PRESET_KEY,
    label: "Handle",
    filter: {
      order_type: ORDERTYPE.backorder,
      order_status: ORDERSTATUS.ordered,
    },
    paramsOverride: { limit: 10000 },
    privilegedOnly: true,
    rendersHandling: true,
  },
  {
    key: APPROVE_PRESET_KEY,
    label: "Approve",
    filter: {
      order_type: ORDERTYPE.backorder,
      order_status: ORDERSTATUS.pending,
    },
    paramsOverride: { limit: 10000 },
    privilegedOnly: true,
    rendersHandling: true,
  },
  {
    key: SEND_PRESET_KEY,
    label: "Send",
    filter: {
      order_type: ORDERTYPE.backorder,
      order_status: ORDERSTATUS.inProgress,
    },
    paramsOverride: { limit: 10000 },
    privilegedOnly: true,
  },
];

export const PROMO_BASE_PARAMS = {
  limit: 50,
  sort: JSON.stringify({ column: "order_date", order: "desc" }),
  include_sub_orders: 1,
};

export function resolvePreset(key: string | null): {
  preset: StandalonePreset | null;
  presetKey: string;
  filter: PromoFilter;
} {
  if (key) {
    const dotIdx = key.indexOf(".");
    if (dotIdx > 0) {
      const prefix = key.slice(0, dotIdx);
      const subKey = key.slice(dotIdx + 1);
      const group = TYPE_GROUPS.find((g) => g.prefix === prefix);
      const sub = STATUS_OPTIONS.find((o) => o.key === subKey);
      if (group && sub) {
        const filter: PromoFilter = { order_type: group.orderType };
        if (sub.status) filter.order_status = sub.status;
        return { preset: null, presetKey: key, filter };
      }
    }
    const standalone = STANDALONE_PRESETS.find((p) => p.key === key);
    if (standalone)
      return {
        preset: standalone,
        presetKey: standalone.key,
        filter: standalone.filter,
      };
  }
  return resolvePreset(DEFAULT_PRESET_KEY);
}
